import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import {
  clearLibraryResultsFreeze,
  getLibraryListQueryKeyPrefix,
  getLibraryResultsFreeze,
  isLibraryResultsFrozen,
  setLibraryResultsFreeze,
} from "@/lib/library-results-freeze";
import type { LibrarySection } from "@/lib/library-search-snapshot";

export type LibraryListPage<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
};

export function useLibraryResultsFreeze<T>(
  section: LibrarySection,
  queryFingerprint: string,
  liveResult: LibraryListPage<T> | undefined,
) {
  const queryClient = useQueryClient();
  const [isFrozen, setIsFrozen] = useState(() =>
    isLibraryResultsFrozen(section, queryFingerprint),
  );

  useEffect(() => {
    setIsFrozen(isLibraryResultsFrozen(section, queryFingerprint));
  }, [section, queryFingerprint]);

  useEffect(() => {
    if (!isFrozen) return;

    const stored = getLibraryResultsFreeze<T>(section);
    if (stored?.fingerprint !== queryFingerprint) {
      clearLibraryResultsFreeze(section);
      setIsFrozen(false);
    }
  }, [isFrozen, section, queryFingerprint]);

  const displayResult = useMemo((): LibraryListPage<T> | undefined => {
    if (isFrozen) {
      const stored = getLibraryResultsFreeze<T>(section);
      if (stored?.fingerprint === queryFingerprint) {
        return {
          items: stored.items,
          total: stored.total,
          page: stored.page,
          pageSize: stored.pageSize,
        };
      }
    }

    return liveResult;
  }, [isFrozen, section, queryFingerprint, liveResult]);

  const freeze = useCallback(() => {
    if (!liveResult || liveResult.items.length === 0) return;

    setLibraryResultsFreeze(section, {
      fingerprint: queryFingerprint,
      items: liveResult.items,
      total: liveResult.total,
      page: liveResult.page,
      pageSize: liveResult.pageSize,
    });
    setIsFrozen(true);
  }, [section, queryFingerprint, liveResult]);

  const unfreeze = useCallback(() => {
    clearLibraryResultsFreeze(section);
    setIsFrozen(false);
    void queryClient.invalidateQueries({
      queryKey: [getLibraryListQueryKeyPrefix(section)],
    });
  }, [section, queryClient]);

  const toggleFreeze = useCallback(() => {
    if (isFrozen) {
      unfreeze();
      return;
    }

    freeze();
  }, [isFrozen, freeze, unfreeze]);

  const canFreeze = Boolean(liveResult && liveResult.items.length > 0);

  return {
    isFrozen,
    displayResult,
    freeze,
    unfreeze,
    toggleFreeze,
    canFreeze,
  };
}
